import { useStripeSubscriptions, useCancelSubscription } from "../../hooks";

export function StripeSubscriptionsCard({ userId }: { userId: string }) {
  const { data: subscriptions = [], isLoading } = useStripeSubscriptions(userId);
  const cancel = useCancelSubscription();
  const handleCancel = (id: string) => {
    if (!window.confirm("Cancel this subscription? This is immediate and cannot be undone.")) return;
    cancel.mutate(id);
  };

  return (
    <div className="border-t pt-4 mt-4">
      <h4 className="text-sm font-semibold text-content-tertiary mb-3">Stripe Subscriptions</h4>
      {isLoading ? (
        <p className="text-xs text-content-tertiary">Loading...</p>
      ) : subscriptions.length === 0 ? (
        <p className="text-xs text-content-tertiary">No active subscriptions</p>
      ) : (
        <div className="space-y-2">
          {subscriptions.map((sub) => (
            <div
              key={sub.id}
              className="flex items-center justify-between bg-surface-tertiary border border-border-secondary rounded-lg px-3 py-2"
            >
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-medium text-content-primary">
                    {sub.planDescription || "Unknown Plan"}
                  </p>
                  <div className="relative group">
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-border-secondary text-content-tertiary text-[10px] font-bold cursor-help">
                      i
                    </span>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block w-64 bg-gray-900 text-white text-xs rounded-lg p-3 shadow-lg z-50">
                      <div className="space-y-1.5">
                        <div><span className="text-gray-400">Subscription ID:</span> <span className="font-mono text-[10px]">{sub.id}</span></div>
                        <div><span className="text-gray-400">Amount:</span> {sub.planAmount != null ? `$${sub.planAmount}` : "—"}{sub.planInterval ? `/${sub.planInterval}` : ""}</div>
                        <div><span className="text-gray-400">Status:</span> <span className={sub.status === "active" ? "text-green-400" : sub.status === "canceled" ? "text-red-400" : "text-amber-400"}>{sub.status}</span></div>
                        {sub.created && (
                          <div><span className="text-gray-400">Started:</span> {new Date(sub.created * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                        )}
                        {sub.currentPeriodStart && sub.currentPeriodEnd && (
                          <div><span className="text-gray-400">Current period:</span> {new Date(sub.currentPeriodStart * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" })} — {new Date(sub.currentPeriodEnd * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                        )}
                        {sub.trialEnd && (
                          <div><span className="text-gray-400">Trial ends:</span> {new Date(sub.trialEnd * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                        )}
                        {sub.canceledAt && (
                          <div><span className="text-gray-400">Canceled:</span> {new Date(sub.canceledAt * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                        )}
                        {sub.cancelAtPeriodEnd && !sub.canceledAt && (
                          <div className="text-amber-300">Cancels at period end</div>
                        )}
                        <div className="pt-1 border-t border-gray-700">
                          <a href={`https://dashboard.stripe.com/subscriptions/${sub.id}`} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 underline">View in Stripe</a>
                        </div>
                      </div>
                      <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-gray-900 rotate-45 -mt-1" />
                    </div>
                  </div>
                </div>
                <p className="text-xs text-content-tertiary">
                  {sub.planAmount != null ? `$${sub.planAmount}` : "—"}
                  {sub.planInterval ? `/${sub.planInterval}` : ""}
                  {" · "}
                  <span
                    className={
                      sub.status === "active"
                        ? "text-success"
                        : sub.status === "canceled"
                          ? "text-error"
                          : "text-warning-dark"
                    }
                  >
                    {sub.status}
                  </span>
                  {sub.cancelAtPeriodEnd && sub.status !== "canceled" && (
                    <span className="text-warning-dark ml-1">(cancels at period end)</span>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <a
                  href={`https://dashboard.stripe.com/subscriptions/${sub.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary hover:text-primary-hover font-medium px-2 py-1 rounded hover:bg-primary/10"
                >
                  View in Stripe
                </a>
                {sub.status === "active" && !sub.cancelAtPeriodEnd && (
                  <button
                    onClick={() => handleCancel(sub.id)}
                    disabled={cancel.isPending && cancel.variables === sub.id}
                    className="text-xs text-error hover:text-error font-medium px-2 py-1 rounded hover:bg-error/10 disabled:opacity-50"
                  >
                    {cancel.isPending && cancel.variables === sub.id ? "Cancelling..." : "Cancel"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
